import React, { ChangeEventHandler, useEffect, useState } from "react";
import dynamic from "next/dynamic";

import { Category } from "@/constants/events";
import FulfillmentMode from "@/custom-types/FulfillmentMode";
import { useEventTracker } from "@/hooks/useEventTracker";
import useQueryParamState from "@/hooks/useQueryParamState";

import FulfillmentInfoBanner from "@/components/FulfillmentInfoBanner";
import IconSvg from "@/components/IconSvg";
import SearchInput from "@/components/SearchInput";
import TrackImpression from "@/components/TrackImpression";

const Toggle = dynamic(() => import("@/components/botanic/Toggle"), {
  ssr: true,
});

const HeaderV2: React.FC<{
  fulfillmentMode: FulfillmentMode;
  hideDivider?: boolean;
  wrapperClassName?: string;
  eventCategory?: Category;
  fullWidthBanner?: boolean;
}> = ({
  fulfillmentMode,
  eventCategory = Category.productFirstShoppingFlow,
  hideDivider,
  wrapperClassName = "mt-lg",
  fullWidthBanner = false,
}) => {
  const { publishEvent } = useEventTracker();

  const [q, setQ] = useQueryParamState("q", { shallow: true });
  // * fulfillmentPreference is consumed in SSR after being set here.
  const [_fulfillmentPreference, setFulfillmentPreference] = useQueryParamState(
    "fulfillmentPreference",
    { scroll: false },
  );

  // * Local component state for toggle and input controls
  const [inputValue, setInputValue] = useState(q || "");
  // * Used to track the initial q parameter on page load
  const [initialSearch, _] = useState(q);

  // * Callbacks
  const onSwitchClick = (selected: string) => {
    publishEvent({
      action: "click",
      category: eventCategory,
      clickedOn: selected,
      label: "fulfillment toggle",
    });
    setFulfillmentPreference(selected);
  };

  const triggerSearch = (value: string) => {
    publishEvent({
      action: "click",
      category: eventCategory,
      label: "search bar",
      // @ts-ignore -- TODO: Fix publishEvent types (https://leafly.atlassian.net/browse/SHH-1133)
      searchedFor: value,
    });
    setQ(value);
  };

  const changeHandler: ChangeEventHandler<HTMLInputElement> = (e) => {
    setInputValue(e.target.value);
  };

  useEffect(() => {
    // * Sync local input state with quey param, if the query param
    // * is updated elsewhere.
    setInputValue(q || "");
  }, [q]);

  return (
    <>
      <FulfillmentInfoBanner
        fulfillmentMode={fulfillmentMode}
        fullWidthBanner={fullWidthBanner}
        eventCategory={eventCategory}
      />
      <div className={`container ${wrapperClassName}`}>
        <div className="grid grid-cols-1 md:gap-xxl md:grid-cols-4 align-middle">
          <TrackImpression
            category={eventCategory}
            label="fulfillment toggle"
            customDimensions={{
              default_option: fulfillmentMode,
            }}
          >
            <Toggle
              aria-label="Toggle Fullfillment Preference"
              options={["pickup", "delivery"]}
              selected={fulfillmentMode}
              onChange={onSwitchClick}
              leftOption={() => ({
                children: (
                  <>
                    <IconSvg width="16" height="16" filePath={"store.svg"} />
                    <div className="ml-1">Pickup</div>
                  </>
                ),
              })}
              rightOption={() => ({
                children: (
                  <>
                    <IconSvg width="16" height="16" filePath={"scooter.svg"} />
                    <div className="ml-1">Delivery</div>
                  </>
                ),
              })}
            />
          </TrackImpression>
          <TrackImpression
            className="mt-lg md:mt-0 md:col-span-3"
            category={eventCategory}
            label="search button"
            customDimensions={{
              default_option: initialSearch,
            }}
          >
            <SearchInput
              inputProps={{
                className: "h-12",
                onChange: changeHandler,
                onKeyDown: (e) => {
                  if (e.key && e.key !== "Enter") {
                    return;
                  }

                  triggerSearch(inputValue);
                },
                placeholder: `Search products for ${
                  fulfillmentMode === "delivery" ? "delivery" : "pickup"
                }`,
                value: inputValue,
              }}
              clearButtonProps={{
                onClick: () => setQ(),
              }}
              searchButtonProps={{
                onClick: () => triggerSearch(inputValue),
              }}
            />
          </TrackImpression>
        </div>
      </div>
      {!hideDivider && (
        <hr className="h-px mt-lg md:mb-sm bg-light-grey border-0"></hr>
      )}
    </>
  );
};

export default HeaderV2;
